import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Account,
    AccountAddress,
    AccountContactPerson,
    AccountContactType,
    AccountSpecialCondition,
    AccountHistorical,
} from '../../../../../shared/types/modules/account';
import {
    asyncCreateAccount,
    asyncGetAccount,
    asyncUpdateAccount,
    asyncGetAccountHistorical,
} from '../../../../../services/accounts.service';
import { FormikProps } from 'formik';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { ACCOUNTS_DETAIL } from '../../../../../routes/paths';
import { ISelect } from '../../../../../shared/types/shared';
import { ACCOUNTS } from '../../../../../routes/paths';

export const useAccountForm = (showHistorical: boolean) => {
    const navigate = useNavigate();
    const { idAccount } = useParams();
    const [account, setAccount] = useState<Account>();
    const [loading, setLoading] = useState(false);
    const [formikContactPerson, setFormikContactPerson] =
        useState<FormikProps<AccountContactPerson>>();
    const [formikAddress, setFormikAddress] =
        useState<FormikProps<AccountAddress>>();
    const [formikSpecialCondition, setFormikSpecialCondition] =
        useState<FormikProps<AccountSpecialCondition>>();
    const [formikContactType, setFormikContactType] =
        useState<FormikProps<AccountContactType>>();
    const [accountHistorical, setAccountHistorical] =
        useState<AccountHistorical[]>();

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const skill = queryParams.get('skill')?.trim();
    const callId = queryParams.get('callId')?.trim();
    const channel = queryParams.get('channel')?.trim();

    const isValidForm =
        formikContactPerson?.isValid &&
        formikAddress?.isValid &&
        formikSpecialCondition?.isValid &&
        formikContactType?.isValid;

    const handleSubmit = async () => {
        await formikContactPerson?.submitForm();
        await formikAddress?.submitForm();
        await formikSpecialCondition?.submitForm();
        await formikContactType?.submitForm();

        if (!isValidForm) return;

        setLoading(true);

        const data: Account = formikContactPerson?.values as Account;
        data.address = formikAddress?.values;
        data.specialCondition = formikSpecialCondition?.values;
        data.contactType = formikContactType?.values;

        let res: Account | undefined = undefined;

        try {
            if (account && account.id)
                res = await asyncUpdateAccount(account.id, data);
            else res = await asyncCreateAccount(data);
        } catch (error) {}

        if (res) {
            ToastSwal.fire({
                text: `¡Felicidades! La cuenta se ${
                    account && account.id ? 'actualizo' : 'creó'
                } satisfactoriamente.`,
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            navigate(
                `${ACCOUNTS_DETAIL}/${res.id}?skill=${skill}&callId=${callId}&channel=${channel}`
            );
        } else {
            ToastSwal.fire({
                text: `Lo sentimos, no se pudo ${
                    account && account.id ? 'actualizar' : 'crear'
                } tu cuenta. Por favor, asegúrate de ${
                    account && account.id
                        ? 'diligenciar los datos correctamente'
                        : 'que no exista previamente'
                } e intenta nuevamente más tarde.`,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleClean = () => {
        formikContactPerson?.resetForm();
        formikAddress?.resetForm();
        formikSpecialCondition?.resetForm();
        formikContactType?.resetForm();
    };

    useEffect(() => {
        if (Boolean(idAccount?.trim())) {
            setLoading(true);
            asyncGetAccount(idAccount?.trim())
                .then((account) => {
                    if (
                        account?.personType &&
                        (account?.personType as ISelect)?.id
                    )
                        account.personType = (
                            account.personType as ISelect
                        )?.id.toString();
                    if (
                        account?.typeDocument &&
                        (account?.typeDocument as ISelect)?.id
                    )
                        account.typeDocument = (
                            account.typeDocument as ISelect
                        )?.id.toString();
                    if (account?.gender && (account?.gender as ISelect)?.id)
                        account.gender = (
                            account.gender as ISelect
                        )?.id.toString();
                    if (
                        account?.address?.city &&
                        (account?.address?.city as ISelect)?.id
                    )
                        account.address.city = (
                            account.address?.city as ISelect
                        )?.id.toString();
                    if (
                        account?.contactType?.typeContactPerson &&
                        (account?.contactType?.typeContactPerson as ISelect)?.id
                    )
                        account.contactType.typeContactPerson = (
                            account.contactType?.typeContactPerson as ISelect
                        )?.id.toString();

                    setAccount(account);
                })
                .finally(() => setLoading(false));
        }
    }, [idAccount]);

    useEffect(() => {
        if (Boolean(idAccount?.trim()) && showHistorical) {
            asyncGetAccountHistorical(idAccount?.trim()).then((resp) => {
                if (resp) setAccountHistorical(resp);
                else navigate(ACCOUNTS);
            });
        }
    }, [showHistorical]);

    return {
        account,
        loading,
        isValidForm,
        handleSubmit,
        handleClean,
        setFormikContactPerson,
        setFormikAddress,
        setFormikSpecialCondition,
        setFormikContactType,
        accountHistorical,
    };
};
